import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { f2ptbr2, str2ptbrDate, toFloat } from '@helpers/functions';
import { AlertService } from '@shared/alert/alert.service';
import { UserOptions } from 'jspdf-autotable';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, catchError, map, of } from 'rxjs';
import { DadosColaborador } from 'src/app/models'; // TODO: alterar para interface nova (junto com novas chamadas)
import { environment } from 'src/environments/environment';

const sexo = { M: 'Masculino', F: 'Feminino' };

interface PTResponse {
    colaborador: string;
    valor: string;
    tp_passivo: string;
    competencia: string;
    natureza: string;
}

interface PTContents {
    info: any[];
    data: PTResponse[];
}

interface UserOptionsWithExtras extends UserOptions {
    observacoes?: string;
}

@Injectable({
    providedIn: 'root',
})
export class ColaboradorService {
    private url: string = environment.apiUrl;
    public passivo_trabalhista = new BehaviorSubject<PTContents>(null);
    private colaborador_dados$ = new BehaviorSubject<DadosColaborador | null>(
        null
    );

    constructor(
        private alertService: AlertService,
        private http: HttpClient,
        private toastr: ToastrService,
        public router: Router
    ) {}

    getColaboradorAux() {
        return this.http
            .get(this.url + 'colaboradoresselect/')
            .pipe(
                catchError(
                    this.handleError<Error>(
                        'Carregar formulário',
                        new Error('ErroFormulario')
                    )
                )
            );
    }

    getColaboradorDados() {
        return this.colaborador_dados$.asObservable();
    }
    setColaboradorDados(colab) {
        return this.colaborador_dados$.next(colab);
    }

    colaboradorDados(id) {
        this.colaborador_dados$.next(null);
        return this.http
            .get<DadosColaborador>(`${this.url}colaboradores/${id}/`)
            .pipe(
                map(res => {
                    const parsed = res;
                    parsed['dados_academicos']['producao_academica'].forEach(
                        el => {
                            if (el.Itens.Documento == '')
                                el.Itens.Documento = null;
                        }
                    );
                    this.colaborador_dados$.next(parsed);
                    return parsed;
                }),
                catchError(err => {
                    this.alertService.error(err.message);
                    return of<DadosColaborador>(null);
                })
            );
    }

    PassivoTrabalhista = {
        endpoint: this.url + 'colaboradores/encargos/busca/',
        get: (query = {}) =>
            this.http
                .get<any[]>(this.url + 'colaboradores/encargos/busca/', {
                    params: query,
                })
                .pipe(
                    map(res => {
                        this.passivo_trabalhista.next({
                            info: [
                                {
                                    href: 'FCPT',
                                    title: 'FCPT',
                                    total: res['total_fcpt'],
                                    description: res['descricao_fcpt'],
                                },
                                {
                                    href: 'Provisões',
                                    title: 'Provisões',
                                    total: res['total_provisoes'],
                                    description: res['descricao_provisoes'],
                                },
                                {
                                    href: 'Saldo FCPT',
                                    title: 'Saldo FCPT',
                                    total: res['total_saldo_fcpt'],
                                    description: res['descricao_saldo_fcpt'],
                                },
                            ],
                            data: res['data'],
                        });
                        return this.passivo_trabalhista.value;
                    }),
                    catchError((err: HttpResponse<any>) => {
                        console.error(err);
                        this.toastr.error(
                            `Erro ${err.status} - ${JSON.stringify(err.body)}`,
                            '[EncargosComponent.onInit]',
                            { timeOut: 10000 }
                        );
                        return of(null);
                    })
                ),
    };

    getCsvDownload(filter_columns = null) {
        return this.passivo_trabalhista.pipe(map(el => el.data));
    }

    getColaboradorCsv(
        option:
            | 'dados_basicos'
            | 'documentos'
            | 'enderecos'
            | 'dados_bancarios'
            | 'recebimentos'
            | 'dados_academicos'
            | 'formacao'
            | 'producao_academica'
            | 'vinculos'
            | 'passivo_trabalhista'
            | 'passivo_trabalhista_sintetico'
            | 'ocorrencias'
    ) {
        return this.colaborador_dados$.pipe(
            map(el => {
                return this.parseData(el)[option];
            })
        );
    }

    parseData(raw: DadosColaborador, docs = []) {
        let data: { [key: string]: UserOptionsWithExtras };
        try {
            let cidade_origem;
            if (
                raw.dados_basicos.id_cidade_nascimento.nome_cidade
                    .split('-')[0]
                    .trim() === 'Não informado'
            ) {
                cidade_origem = 'Não informado';
            } else {
                cidade_origem =
                    raw.dados_basicos.id_cidade_nascimento.nome_cidade;
            }
            let passivo_sintetico = Object.keys(
                raw.passivo_trabalhista.valores
            ).map(key => [key, raw.passivo_trabalhista.valores[key]]);

            data = {
                dados_basicos: {
                    body: [
                        [
                            `Nome: ${raw.dados_basicos.nome}`,
                            `CPF: ${raw.dados_basicos.cpf.replace(
                                /^(\d{3})(\d{3})(\d{3})(\d{2}).*/,
                                '$1.$2.$3-$4'
                            )}`,
                            `Matrícula: ${raw.dados_basicos.matricula}`,
                        ],
                        [
                            `E-mail: ${raw.dados_basicos.email}`,
                            `E-mail Secundário: ${raw.dados_basicos.email_secundario}`,
                            `Tipo de vínculo: ${raw.dados_basicos.id_tp_vinculo.tp_vinculo}`,
                            `Sexo: ${sexo[raw.dados_basicos.sexo]}`,
                        ],
                        [
                            `País de origem: ${raw.dados_basicos.id_nacionalidade.nome_pais}`,
                            `Cidade de origem: ${cidade_origem}`,
                            `Previsão de conclusão de curso: ${str2ptbrDate(
                                raw.dados_basicos.dt_prev_conclusao
                            )}`,
                        ],
                    ],
                },
                documentos: {
                    head: [['Tipo', 'Número/ano', 'Data']],
                    body: docs.map(el => [
                        el.id_tp_documento.tp_documento,
                        el.num_documento + '/' + el.ano_documento,
                        str2ptbrDate(el.data_documento),
                    ]),
                },
                enderecos: {
                    body: [
                        [
                            `Logradouro: ${raw.endereco?.[0]?.logradouro}`,
                            `Número: ${raw.endereco?.[0]?.numero}`,
                            `Bairro: ${raw.endereco?.[0]?.bairro}`,
                        ],
                        [
                            `Cidade: ${raw.endereco?.[0]?.id_cidade?.nome_cidade}`,
                            `Estado: ${raw.endereco?.[0]?.id_cidade?.id_uf?.nome_uf}`,
                            `CEP: ${raw.endereco?.[0]?.cep}`,
                        ],
                        [
                            `Complemento: ${raw.endereco?.[0]?.complemento}`,
                            `Telefone 1: ${raw.endereco?.[0]?.tel1}`,
                            `Telefone 2: ${raw.endereco?.[0]?.tel2}`,
                        ],
                    ],
                    observacoes: raw.endereco?.[0]?.observacoes || ' - ',
                },
                dados_bancarios: {
                    body: [
                        [
                            `Banco: ${raw.financeiro.dados_bancarios[0]?.id_banco.nome_banco}`,
                            `Tipo de conta: ${raw.financeiro.dados_bancarios[0]?.id_tp_conta.tp_conta}`,
                            '\t\t\t\t',
                        ],
                        [
                            `Conta: ${raw.financeiro.dados_bancarios[0]?.conta}`,
                            `Agência: ${raw.financeiro.dados_bancarios[0]?.agencia}`,
                            `Operação: ${raw.financeiro.dados_bancarios[0]?.operacao}`,
                        ],
                    ],
                },
                recebimentos: {
                    head: [
                        [
                            'Data',
                            'Tipo de folha de pagamento',
                            'Competência',
                            'Projeto',
                            'Valor',
                        ],
                    ],
                    body: raw.financeiro.recebimentos.map(el => [
                        str2ptbrDate(el.data_pagamento),
                        el['Tipo de folha'],
                        el['Competência'],
                        el['Projeto'],
                        f2ptbr2.format(el['Valor total']),
                    ]),
                    foot: [
                        [
                            { content: 'Total', colSpan: 4 },
                            {
                                content: f2ptbr2.format(
                                    raw.financeiro.recebimentos.reduce(
                                        (acc, red) => acc + red['Valor total'],
                                        0
                                    )
                                ),
                            },
                        ],
                    ],
                },
                dados_academicos: {
                    body: [
                        [
                            `Lattes: ${raw.dados_academicos.lattes}`,
                            `Maior formação: ${raw.dados_academicos.maior_formacao}`,
                            '\t\t\t',
                        ],
                    ],
                },
                formacao: {
                    head: [
                        [
                            'Ano',
                            'Tipo de formação',
                            'Área do conhecimento',
                            'País',
                            'UF',
                            'Concluído',
                        ],
                    ],
                    body: raw.dados_academicos.formacao.map(el => {
                        let ano_conclusao = el.ano_conclusao
                            ? el.ano_conclusao.split('-')[0]
                            : '-';
                        return [
                            ano_conclusao,
                            el.id_tp_formacao.tp_formacao,
                            el.id_area_conhecimento.area_conhecimento,
                            el.id_pais.nome_pais,
                            el.id_uf.nome_uf,
                            el.concluido ? 'Sim' : 'Não',
                        ];
                    }),
                },
                producao_academica: {
                    head: [['#', 'Ano', 'Tipo', 'Título', 'Autoria']],
                    body: raw.dados_academicos.producao_academica.map(
                        (el, idx) => [
                            idx + 1,
                            el['Ano'],
                            el['Tipo'],
                            el['Título'],
                            el['Itens']['Autores'].find(
                                autor =>
                                    autor.autor === raw.dados_basicos['nome']
                            ).funcao || 'Não encontrado',
                        ]
                    ),
                },
                vinculos: {
                    head: [
                        [
                            'Projeto',
                            'Função',
                            'CH semanal',
                            'Valor',
                            'Início vigência',
                            'Fim vigência',
                        ],
                    ],
                    body: raw.vinculos.map(el => [
                        el.id_projeto.titulo_curto,
                        el.id_funcao_colaborador_projeto
                            .funcao_colaborador_projeto,
                        el.chs,
                        f2ptbr2.format(el.valor),
                        str2ptbrDate(el.inicio_vigencia),
                        str2ptbrDate(el.fim_vigencia),
                    ]),
                },
                passivo_trabalhista: {
                    head: [
                        [
                            'Competência',
                            'Projeto',
                            'Passivo',
                            'Natureza',
                            'Valor',
                        ],
                    ],
                    body: raw.passivo_trabalhista.passivo_trabalhista.map(
                        el => [
                            el.competencia,
                            el.projeto,
                            el.passivo,
                            el.natureza,
                            f2ptbr2.format(el.valor),
                        ]
                    ),
                },
                passivo_trabalhista_sintetico: {
                    head: [['Passivo', 'Valor']],
                    body: passivo_sintetico,
                    foot: [
                        [
                            'Total',
                            f2ptbr2.format(
                                passivo_sintetico.reduce(
                                    (acc, red) => acc + toFloat(red[1]),
                                    0
                                )
                            ),
                        ],
                    ],
                },
                ocorrencias: {
                    head: [
                        ['Tipo de ocorrência', 'Descrição', 'Início', 'Fim'],
                    ],
                    body: raw.ocorrencias.map(el => [
                        el.id_tp_ocorrencia_colaborador
                            .tp_ocorrencia_colaborador,
                        el.descricao,
                        str2ptbrDate(el.dt_ini_ocorrencia),
                        str2ptbrDate(el.dt_fim_ocorrencia),
                    ]),
                },
            };
        } catch (error) {
            return new Error(error);
        }

        return data;
    }

    // Colaborador Dados

    getColaborador = (id: number) =>
        this.http.get<any>(`${this.url}colaboradorcrud/${id}/`);
    getEndereco = (id: number) =>
        this.http.get<any>(`${this.url}colaboradoresenderecos/${id}/`);
    getConta = (id: number) =>
        this.http.get<any>(
            `${this.url}financas/colaboradoresdadosbancarios/${id}/`
        );
    getFormacao = (id: number) =>
        this.http.get<any>(`${this.url}colaboradoresformacao/${id}/`);
    getVinculos = (id: number) =>
        this.http.get<any>(`${this.url}colaboradoresvinculos/${id}/`);
    getOcorrencias = (id: number) =>
        this.http.get<any>(`${this.url}colaboradoresocorrencias/${id}/`);

    private handleError<T>(operation = 'operation', result?: T) {
        return (error: any): Observable<T> => {
            console.error('API error', error);
            this.alertService.error(`Erro na operação: ${operation}`);
            return of(result as T);
        };
    }
}
